import {
  ApplicationDetailLayout,
  CircularLoader,
  EntityHeader,
  ErrorPageLayout,
} from '@converge/ui';
import { ConvergeMatchUrls, useConvergeNavigation } from '@converge/ui';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  GetQuoteQuery,
  NavigationItem,
  useQuoteQuery,
} from '@converge/graphql';
import { Outlet, useMatch, useParams } from 'react-router-dom';

import { QUOTE_ENTITY_DETAIL_MENU_ITEMS } from '@converge/data';

type QuotesEntityDetailParams = {
  quoteUuid: string;
};

const QuotesEntityDetail = () => {
  const {
    selectedLeftNavItem,
    setSelectedLeftNavItem,
    leftNavItems,
    setLeftNavItems,
    sectionId,
    quoteUuid,
  } = useNav();

  const {
    loading: quoteEntityLoading,
    data: quoteEntityData,
    error: quoteEntityError,
  } = useQuoteQuery(quoteUuid);
  useEffect(() => {
    if (sectionId) {
      const find = leftNavItems.find((e) => e.value === sectionId);
      if (find) {
        setSelectedLeftNavItem(find);
      }
    }
  }, [sectionId, setSelectedLeftNavItem, leftNavItems]);

  if (quoteEntityLoading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  if (quoteEntityError) {
    return (
      <ErrorPageLayout
        message={`Error getting insured entity: ${quoteEntityError.message}`}
        messageId="quote.detail.page.error"
      />
    );
  }

  if (!quoteEntityData) {
    return (
      <ErrorPageLayout
        message="Insured Entity not found."
        messageId="quote.detail.page.quoteEntityNotFound"
      />
    );
  }

  return (
    <QuotesEntityDetailInner
      quoteEntity={quoteEntityData}
      selectedLeftNavItem={selectedLeftNavItem}
      setSelectedLeftNavItem={setSelectedLeftNavItem}
      leftNavItems={leftNavItems}
      setLeftNavItems={setLeftNavItems}
      sectionId={sectionId}
    />
  );
};

interface IQuoteEntityDetailInnerProps {
  quoteEntity: GetQuoteQuery;
  selectedLeftNavItem: NavigationItem;
  setSelectedLeftNavItem: (item: NavigationItem) => void;
  leftNavItems: NavigationItem[];
  setLeftNavItems: (item: NavigationItem[]) => void;
  sectionId?: string;
}

const QuotesEntityDetailInner = ({
  quoteEntity,
  leftNavItems,
  selectedLeftNavItem,
  setSelectedLeftNavItem,
}: IQuoteEntityDetailInnerProps) => {
  const navigate = useConvergeNavigation();

  return (
    <Fragment>
      <EntityHeader
        referenceId={quoteEntity.quote?.referenceId}
        title="Quote"
      />
      <ApplicationDetailLayout
        leftNavItems={leftNavItems}
        onLeftNavItemSelected={(item) => {
          setSelectedLeftNavItem(item);
          navigate.quoteEntityDetail({
            quoteUuid: quoteEntity.quote?.uuid as string,
            sectionUrl: item.value,
          });
        }}
        selectedLeftNavItem={selectedLeftNavItem}
      >
        <Outlet
          context={{
            quoteUuid: quoteEntity.quote?.uuid as string,
            quoteDetails: quoteEntity,
            selectedLeftNavItem,
          }}
        />
      </ApplicationDetailLayout>
    </Fragment>
  );
};

export default QuotesEntityDetail;

const useNav = () => {
  const { quoteUuid } = useParams<keyof QuotesEntityDetailParams>();

  let sectionId = useMatch(ConvergeMatchUrls.quoteEntityDetail())?.params[
    'sectionId'
  ];

  const { defaultSelectedLeftNavItem } = useMemo(() => {
    return {
      defaultSelectedLeftNavItem:
        QUOTE_ENTITY_DETAIL_MENU_ITEMS.find(
          (item) => item.value === sectionId
        ) || QUOTE_ENTITY_DETAIL_MENU_ITEMS[0],
    };
  }, [sectionId]);

  const [selectedLeftNavItem, setSelectedLeftNavItem] =
    useState<NavigationItem>(defaultSelectedLeftNavItem);
  const [leftNavItems, setLeftNavItems] = useState<NavigationItem[]>(
    QUOTE_ENTITY_DETAIL_MENU_ITEMS
  );

  return {
    selectedLeftNavItem,
    setSelectedLeftNavItem,
    leftNavItems,
    setLeftNavItems,
    sectionId,
    quoteUuid,
  };
};
